import {
    createContext,
    Dispatch,
    SetStateAction,
    useMemo,
    useState,
    Context,
    useCallback,
    useContext
} from "react";
import { Sort } from "@models";
import { FCX } from "@datassential/platform-ui-lib";
import { getFilterSort } from "@hooks/useFilterSort";

export type FilterSortContextValue<T> = {
    key: keyof T;
    setKey: Dispatch<SetStateAction<keyof T>>;
    direction: Sort;
    setDirection: Dispatch<SetStateAction<Sort>>;
    query: string;
    setQuery: Dispatch<SetStateAction<string>>;
};

export function filterSortContextFactory<T>(): {
    Context: Context<FilterSortContextValue<T>>;
    Provider: FCX<{
        initialSortingKey: keyof T;
        initialSortingDirection?: Sort;
    }>,
} {
    const FILTER_SORT_CONTEXT_DEFAULTS: FilterSortContextValue<T> = {
        key: '' as keyof T,
        setKey: () => null,
        direction: Sort.Desc,
        setDirection: () => null,
        query: '',
        setQuery: () => null,
    };

    const Ctx = createContext<FilterSortContextValue<T>>(FILTER_SORT_CONTEXT_DEFAULTS);
    return {
        Context: Ctx,
        Provider: ({
            children,
            initialSortingKey,
            initialSortingDirection = Sort.Desc,
        }) => {
            const [key, setKey] = useState<keyof T>(initialSortingKey);
            const [direction, setDirection] = useState<Sort>(initialSortingDirection);
            const [query, setQuery] = useState<string>('');

            const value = useMemo(
                () => ({
                    key,
                    setKey,
                    direction,
                    setDirection,
                    query,
                    setQuery,
                }),
                [key, setKey, direction, setDirection, query, setQuery]
            );

            return (
                <Ctx.Provider value={value}>
                    {children}
                </Ctx.Provider>
            );
        }
    };
}

export function createFilterSortWithContext<ItemType>(context: Context<FilterSortContextValue<ItemType>>) {
    return function useFilterSort(
        rawData: ItemType[],
        searchKeys?: keyof ItemType | Array<keyof ItemType>,
    ) {
        const {
            key, setKey,
            direction, setDirection,
            query, setQuery,
        } = useContext(context);

        const data = useMemo(
            () => getFilterSort({
                data: rawData,
                query,
                searchKeys,
                sortingKey: key,
                sortingDirection: direction,
            }),
            [rawData, query, searchKeys, key, direction]
        );

        const sort = useCallback(
            (rawData: ItemType[]) => getFilterSort({
                data: rawData,
                query,
                searchKeys,
                sortingKey: key,
                sortingDirection: direction,
            }),
            [query, searchKeys, key, direction]
        );

        return {
            data,
            sortingKey: key,
            setSortingKey: setKey,
            sortingDirection: direction,
            setSortingDirection: setDirection,
            query,
            setQuery,
            sort,
        };
    }
}